import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { USER_ROLES } from '../../constants';
import { Loading } from '../ui/Loading';
import { DEFAULT_PAID_PLAN } from '../../modules/onboarding/services/onboardingService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: keyof typeof USER_ROLES;
  requiredPermissions?: string[];
  fallbackPath?: string;
  /** Require an active subscription plan to access the route */
  requireActivePlan?: boolean;
  /** Path to redirect when no active plan (default: '/payment?plan=producer') */
  paymentRedirectPath?: string;
  /** Require a complete profile to access the route */
  requireCompleteProfile?: boolean;
}

/**
 * Protected Route Component
 * 
 * This component provides route-level authentication and authorization.
 * It checks if the user is authenticated and optionally verifies roles and permissions.
 * 
 * @param children - The component to render if access is granted
 * @param requiredRole - Optional role requirement (e.g., 'ADMIN', 'USER')
 * @param requiredPermissions - Optional array of required permissions
 * @param fallbackPath - Path to redirect to if access is denied (default: '/auth')
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermissions = [],
  fallbackPath = '/auth',
  requireActivePlan = false,
  paymentRedirectPath = `/payment?plan=${DEFAULT_PAID_PLAN}`,
  requireCompleteProfile = false,
}) => {
  const { 
    isLoggedIn, 
    userRole, 
    hasPermission, 
    authState, 
    isTokenExpired 
  } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  // Only show if not authenticated yet to avoid blocking navigation for logged users
  if (authState.isLoading && !authState.isAuthenticated) {
    return (
      <Loading message="Verificando autenticação..." fullScreen={true} />
    );
  }

  // Redirect to auth if not logged in
  if (!isLoggedIn) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check role requirement
  if (
    requiredRole &&
    userRole !== USER_ROLES.ADMIN &&
    userRole !== USER_ROLES[requiredRole]
  ) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '50vh',
          gap: 2,
          textAlign: 'center',
          p: 3
        }}
      >
        <Typography variant="h5" color="error">
          Acesso Negado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Você não tem permissão para acessar esta página.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Função necessária: {requiredRole}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sua função atual: {userRole || 'Nenhuma'}
        </Typography>
      </Box>
    );
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const missingPermissions = requiredPermissions.filter(
      permission => !hasPermission(permission)
    );

    if (missingPermissions.length > 0) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '50vh',
            gap: 2,
            textAlign: 'center',
            p: 3
          }}
        >
          <Typography variant="h5" color="error">
            Permissões Insuficientes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Você não tem as permissões necessárias para acessar esta página.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Permissões necessárias: {missingPermissions.join(', ')}
          </Typography>
        </Box>
      );
    }
  }

  // Require active subscription plan
  if (requireActivePlan && !authState.hasActivePlan) {
    return (
      <Navigate 
        to={paymentRedirectPath}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Require complete profile
  if (requireCompleteProfile && !authState.hasCompleteProfile) {
    return (
      <Navigate
        to={'/profile/complete'}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // All checks passed, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
